/**
 * 画板 GeoJSON 图层 / 测距标注 / 文字 Marker / 草稿预览同步
 */
import { useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { toMapLngLat } from '../utils/dashboard/basemapCoord';
import {
  SOURCE_ID,
  DEFAULT_TEXT_STYLE,
  clickToWgs84,
  buildTextLabelElement,
  featuresToGeoJSON
} from '../utils/mapDrawingBoard';
import { ensureDrawLayers } from '../utils/mapDrawingBoard/ensureLayers';
import {
  clearMeasureMarkers as clearMeasureMarkersUtil,
  refreshMeasureLabels as refreshMeasureLabelsUtil
} from '../utils/mapDrawingBoard/measureLabels';
import { applyDraftPreview } from '../utils/mapDrawingBoard/syncPreview';

export default function useMapDrawingLayers({
  getMap,
  onStartInteract,
  measureMarkersRef,
  textMarkersRef,
  featuresRef,
  draftRef,
  toolRef,
  selectedIdRef,
  selectedIdsRef,
  suppressClickRef,
  sketchWidthRef,
  sketchPreviewColorRef,
  setSelectedId,
  setExpanded,
  setPinned,
  setDrawColor,
  setDrawOpacity,
  setTextContent,
  setTextStyle,
  setStatusText,
  setTextEditOpen,
  setFeatures,
  setDraftPoints
}) {
  const ensureLayers = useCallback((map) => ensureDrawLayers(map), []);

  const clearMeasureMarkers = useCallback(() => {
    clearMeasureMarkersUtil(measureMarkersRef);
  }, [measureMarkersRef]);

  /** 测距：边长 + 总长；区域/矩形：各边长 + 框内面积 */
  const refreshMeasureLabels = useCallback((map, list, draftPts, draftKind) => {
    refreshMeasureLabelsUtil(map, measureMarkersRef, list, draftPts, draftKind);
  }, [measureMarkersRef]);

  /** 同步文字 HTML Marker（与 GeoJSON 锚点分离，方便样式与拖动） */
  const syncTextMarkers = useCallback((map, list, selected = selectedIdRef.current) => {
    if (!map) return;
    textMarkersRef.current.forEach((marker) => {
      try { marker.remove(); } catch (_) { /* ignore */ }
    });
    textMarkersRef.current.clear();

    const onSelect = (id, opts = {}) => {
      onStartInteract?.();
      setSelectedId(id);
      setExpanded(true);
      setPinned(true);
      const feat = featuresRef.current.find((f) => f.id === id);
      if (feat?.type === 'text') {
        setDrawColor(feat.color || DEFAULT_TEXT_STYLE.color);
        setDrawOpacity(feat.opacity ?? DEFAULT_TEXT_STYLE.opacity);
        setTextContent(feat.text || '');
        setTextStyle({
          fontFamily: feat.fontFamily || DEFAULT_TEXT_STYLE.fontFamily,
          fontSize: feat.fontSize || DEFAULT_TEXT_STYLE.fontSize,
          letterSpacing: feat.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing,
          lineHeight: feat.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight,
          color: feat.color || DEFAULT_TEXT_STYLE.color,
          bgColor: feat.bgColor || DEFAULT_TEXT_STYLE.bgColor,
          bgEnabled: feat.bgEnabled !== false,
          opacity: feat.opacity ?? DEFAULT_TEXT_STYLE.opacity
        });
        setStatusText('已选中文字：可改样式或内容；Del 删除');
        if (opts.edit) setTextEditOpen(true);
      }
    };

    (list || []).filter((f) => f.type === 'text').forEach((feat) => {
      const wgs = feat.coordinates?.[0];
      if (!wgs) return;
      const display = toMapLngLat(wgs[0], wgs[1], undefined, map) || wgs;
      const el = buildTextLabelElement(feat, {
        selected: selected === feat.id,
        onSelect
      });
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center', draggable: true })
        .setLngLat(display)
        .addTo(map);
      marker.on('dragstart', () => {
        onStartInteract?.();
        setSelectedId(feat.id);
        suppressClickRef.current = true;
      });
      marker.on('dragend', () => {
        const ll = marker.getLngLat();
        const wgsPt = clickToWgs84(ll);
        const next = featuresRef.current.map((f) => (
          f.id === feat.id ? { ...f, coordinates: [wgsPt] } : f
        ));
        setFeatures(next);
        featuresRef.current = next;
      });
      textMarkersRef.current.set(feat.id, marker);
    });
  }, [onStartInteract]);

  const clearTextMarkers = useCallback(() => {
    textMarkersRef.current.forEach((marker) => {
      try { marker.remove(); } catch (_) { /* ignore */ }
    });
    textMarkersRef.current.clear();
  }, [textMarkersRef]);

  const syncFeaturesToMap = useCallback((map, list) => {
    if (!map) return;
    if (!ensureLayers(map)) return;
    try {
      const src = map.getSource(SOURCE_ID);
      if (src) src.setData(featuresToGeoJSON(list || [], map, selectedIdsRef.current));
    } catch (err) {
      console.warn('同步画板图层失败:', err?.message || err);
      return;
    }
    const drafting = ['measure', 'polygon', 'rectangle'].includes(toolRef.current);
    refreshMeasureLabels(
      map,
      list || [],
      drafting ? draftRef.current : [],
      toolRef.current
    );
    try {
      syncTextMarkers(map, list || [], selectedIdRef.current);
    } catch (_) { /* ignore */ }
  }, [ensureLayers, refreshMeasureLabels, syncTextMarkers]);

  const syncPreview = useCallback((map, points, kind) => {
    if (!map) return;
    if (!ensureLayers(map)) return;
    const result = applyDraftPreview(map, points, kind, {
      sketchWidth: sketchWidthRef.current,
      sketchColor: sketchPreviewColorRef.current
    });
    if (result === false) return;
    if (result === 'empty') {
      if (kind !== 'sketch') {
        refreshMeasureLabels(map, featuresRef.current, [], kind);
      }
      return;
    }
    if (result === 'sketch') return;
    refreshMeasureLabels(map, featuresRef.current, points, kind);
  }, [ensureLayers, refreshMeasureLabels]);

  const clearPreview = useCallback(() => {
    const map = getMap?.();
    setDraftPoints([]);
    draftRef.current = [];
    if (map) syncPreview(map, [], toolRef.current);
  }, [getMap, syncPreview, setDraftPoints, draftRef, toolRef]);

  return {
    ensureLayers,
    clearMeasureMarkers,
    refreshMeasureLabels,
    syncTextMarkers,
    clearTextMarkers,
    syncFeaturesToMap,
    syncPreview,
    clearPreview
  };
}
